import React, { useState, useRef, useEffect } from 'react'

import {
  useDocument,
  WebRendererArtboard,
} from '@sketch-hq/sketch-web-renderer'

import Artboard from './Artboard'
import { useHandleArtboardsInteractions } from '../../hooks'
import { useStableHandler } from '@sketch/utils'

interface ArtboardsInfoOverlayProps {
  disablePointerCursor?: boolean
}

/**
 * This is an overlay that renders the artboards' names,
 * cta, outline and tracks interactions on them.
 */
const ArtboardsInfoOverlay = ({
  disablePointerCursor,
}: ArtboardsInfoOverlayProps) => {
  const [selectedArtboardUUID, setSelectedArtboardUUID] = useState<
    string | null
  >(null)

  const document = useDocument()!

  const { onGoToArtboard } = useHandleArtboardsInteractions()
  const artboardsRefs = useRef<Record<string, HTMLDivElement | null>>({})

  const handleClick = useStableHandler((artboard: WebRendererArtboard) => {
    // do not select Artboards while placing a new annotation
    if (disablePointerCursor) return

    if (selectedArtboardUUID !== artboard.objectId)
      setSelectedArtboardUUID(artboard.objectId)
  })

  const handleDoubleClick = useStableHandler(
    (artboard: WebRendererArtboard) => {
      onGoToArtboard?.(artboard.objectId, 'doubleClick')
    }
  )

  const handleCTAClick = useStableHandler((artboard: WebRendererArtboard) => {
    onGoToArtboard?.(artboard.objectId, 'CTA')
  })

  const handleWindowKeyPress = useStableHandler((event: KeyboardEvent) => {
    // do not go to Artboard view while placing new annotation
    if (disablePointerCursor) return

    if (
      selectedArtboardUUID &&
      event.key === 'Enter' &&
      global.document.activeElement
    ) {
      onGoToArtboard?.(selectedArtboardUUID, 'keyPressEnter')
    }
  })

  const handleWindowClick = useStableHandler(
    (event: MouseEvent | TouchEvent) => {
      // deselect active artboard when we start placing an annotation
      if (disablePointerCursor && selectedArtboardUUID) {
        setSelectedArtboardUUID(null)
      }

      // Checking if the click on the overlay was inside one of the artboards,
      // If not, we deselect the selected artboard
      for (const artboardId in artboardsRefs.current) {
        if (artboardsRefs.current[artboardId]?.contains(event.target as Node)) {
          return
        }
      }

      if (selectedArtboardUUID) setSelectedArtboardUUID(null)
    }
  )

  // Attaching a KeyPress event to the body, so we can detect when the user presses
  // the Enter key and launch the onGoToArtboard event if there is an artboard selected
  useEffect(() => {
    window.document.body.addEventListener('keypress', handleWindowKeyPress)
    window.document.body.addEventListener('click', handleWindowClick)

    return () => {
      window.document.body.removeEventListener('keypress', handleWindowKeyPress)
      window.document.body.removeEventListener('click', handleWindowClick)
    }
  }, [handleWindowClick, handleWindowKeyPress])

  return (
    <>
      {document.artboards.map(artboard => (
        <Artboard
          ref={ref => (artboardsRefs.current[artboard.objectId] = ref)}
          key={artboard.objectId}
          onDoubleClick={handleDoubleClick}
          onCTAClick={handleCTAClick}
          artboard={artboard}
          document={document}
          selected={selectedArtboardUUID === artboard.objectId}
          onClick={handleClick}
          disablePointerCursor={disablePointerCursor}
        />
      ))}
    </>
  )
}

export default React.memo(ArtboardsInfoOverlay)
